<!-- 
Panel for displaying details of information sessions.
-->
<template>
  <v-card flat>
    <v-card-text>
      <our-loading-indicator v-if="state.loading"/>
      <v-container v-else fluid grid-list-md class="pa-0">
        <v-layout row wrap>
          <v-flex v-if="canEdit && loaded" xs12 sm6 md4>
            <v-card>
              <v-card-title class="subheading pb-0">New information session</v-card-title>
              <div style="overflow-y: hidden; height: 224px;">
                <v-card-text v-if="canScheduleNewSession">
                  <p>
                    To include {{ (hasSessions ? "another" : "an") }} information session in the levy collection, edit the 
                    <router-link :to="`/levy-collections/${levyCollectionId}/setup/notices/InformationSessionsNotice`">information session notice template</router-link>
                    with the details you want to send to providers and then schedule the timing of the session by clicking the button below.
                  </p>
                  <p>
                    You will be able to send the notice to eligible providers using the <i>send information session notice</i> 
                    workflow task until the session end date is reached.
                  </p>
                </v-card-text>
                <v-card-text v-else>
                  <p>
                    You will be able to schedule another information session once the current session has ended.
                  </p>
                </v-card-text>
              </div>
              <v-card-actions>
                <v-spacer/>
                <v-btn 
                  :disabled="!canScheduleNewSession"
                  flat color="primary" class="ml-0" 
                  @click="showCreateDialog = true"
                >
                  {{ buttonCaption }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-flex>
          <v-flex
            v-for="session in sessions"
            :key="session.id"
            xs12 sm6 md4
          >
            <v-card>
              <v-card-title class="subheading">
                {{ session.number | ordinal }} information session
                <v-chip 
                  v-if="session.hasExpired" 
                  small color="grey" text-color="white" class="ml-3"
                >Ended</v-chip>
                <v-chip 
                  v-else 
                  small color="green" text-color="white" class="ml-3"
                >Current</v-chip>
              </v-card-title>
              <div style="overflow-y: hidden; height: 200px;">
                <v-card-text>
                  <our-data-container dense>
                    <our-data-field label="Ends">
                      {{ session.expiryDate | longDate }}
                    </our-data-field>
                    <our-data-field label="Sent to">
                      {{ pluralise(session.noticesSent) }}
                    </our-data-field>
                    <div v-if="!session.hasExpired" class="caption mt-3">
                      You can send information session notices from the
                      <router-link :to="`/levy-collections/${levyCollectionId}/workflow`">levy collection workflow</router-link>
                      screen.
                    </div>
                  </our-data-container>
                </v-card-text>
              </div>
              <v-card-actions style="height: 52px;">
                <v-btn 
                  v-if="canEdit && session.isLast && session.noticesSent == 0"
                  :disabled="state.loading" 
                  :loading="saving"
                  flat color="error" 
                  @click="deleteSession(session.id)"
                >
                  Cancel
                </v-btn>
                <v-spacer/>
                <v-btn 
                  v-if="canEdit && session.isLast"
                  :disabled="state.loading" 
                  flat color="primary" 
                  @click="edit(session.id)"
                >
                  Change end date
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-flex>
        </v-layout>
      </v-container>
    </v-card-text>
    <information-session-dialog v-model="showCreateDialog" :levy-collection-id="levyCollectionId" />
    <information-session-dialog v-model="showEditDialog" :levy-collection-id="levyCollectionId" :information-session-id="informationSessionId" />
  </v-card>
</template>

<script>
import * as action from "@/store/levy-collections/action-types";
import * as claim from "@/store/claim-types";
import InformationSessionDialog from "@/components/levy-collection-setup/InformationSessionDialog.vue";
import filterMixins from "@/mixins/filter.mixins";

export default {
  components: { InformationSessionDialog },
  mixins: [filterMixins],
  props: {
    levyCollectionId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      showCreateDialog: false,
      showEditDialog: false,
      informationSessionId: null,
      headers: [
        { text: "", value: "Id", class: "navCol", width: 0, sortable: false },
        { text: "Number", value: "Number", sortable: false, width: 100 },
        { text: "Expiry date", value: "ExpiryDate", sortable: false },
        { text: "", value: "Actions", sortable: false }
      ]
    };
  },
  computed: {
    state() {
      return this.$store.state.levyCollections.viewInformationSessions;
    },
    loaded() {
      return this.state.loaded;
    },
    saving() {
      return this.$store.state.levyCollections.editInformationSession.saving;
    },
    sessions() {
      return this.state.data;
    },
    canEdit() {
      return (
        this.$store.getters.claims.includes(claim.LEVY_COLLECTION_CONFIGURE_INFORMATION_SESSIONS) &&
        this.$store.state.levyCollections.view.data.status != "Finalised"
      );
    },
    hasSessions() {
      return this.state.data.length > 0;
    },
    canScheduleNewSession() {
      return !this.hasSessions || this.sessions.every(s => s.hasExpired);
    },
    buttonCaption() {
      return this.hasSessions ? "Schedule another" : "Schedule";
    }
  },
  watch: {
    levyCollectionId() {
      this.reload();
    },
    loaded() {
      this.reload();
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    reload() {
      const needsReloaded =
        (!this.loaded && !this.state.loading) || this.levyCollectionId != this.state.levyCollectionId;
      if (needsReloaded) {
        this.load();
      }
    },
    load() {
      this.$store.dispatch(action.VIEW_INFORMATION_SESSIONS, this.levyCollectionId);
    },
    edit(informationSessionId) {
      this.informationSessionId = informationSessionId;
      this.showEditDialog = true;
    },
    deleteSession(informationSessionId) {
      this.$store.dispatch(action.EDIT_INFORMATION_SESSION_DELETE, {
        levyCollectionId: this.levyCollectionId,
        informationSessionId
      });
    },
    pluralise(count) {
      if (count == 0) {
        return "Nobody";
      } else if (count == 1) {
        return "1 provider";
      } else {
        return `${count} providers`;
      }
    }
  }
};
</script>

<style lang="scss">
td.active {
  color: green;
}
</style>